import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import APIInvoke from '../../../utils/APIInvoke';
import { DateTime } from 'luxon';
import { IconButton } from '@mui/material';
import ConfirmDialog from '../../../template/ConfirmDialog';
import LaunchIcon from '@mui/icons-material/Launch';
import { useLocation, useNavigate } from 'react-router-dom';

const Heatmap2d = ({ rawData = [] , isRunning, loading, project }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(undefined);
    const [timelapse, setTimelapse] = useState([]);
    const [showTimelapseDialog, setShowTimelapseDialog] = useState({
        show: false,
        startDate: null,
        endDate: null
    });

    useEffect(() => {
        const heatmapData = rawData.map(item => {
            const { day, hour, minute, collection } = item._id;
            const date = DateTime.fromISO(`${day}T${hour.toString().padStart(2, '0')}:00:00`, { zone: 'UTC' }).setZone('local');
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayLabel = `${dayNames[date.weekday % 7]} ${date.day}`;
            const eventCount = Number(item.eventCount) || 0;
            return [date.hour, dayLabel, eventCount, collection, date];
        });
        setData(heatmapData);
    }, [rawData]);


    const formatNumber = (value) => {
        if (value < 1000) return value.toString();
        if (value < 1000000) return `${(value / 1000).toFixed(1)}k`;
        if (value < 1000000000) return `${(value / 1000000).toFixed(1)}M`;
        return `${(value / 1000000000).toFixed(1)}B`;
    };

    const getOption = () => {
        const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}`);

        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i); // Empezamos en el día actual y restamos hasta -6
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            return `${dayNames[date.getDay()]} ${date.getDate()}`;
        }).reverse();

        return {
            tooltip: {
                position: 'top',
                formatter: (params) => {
                    const [time, day, value, collection] = params.data;
                    return `Events: ${formatNumber(value)}<br>${day}-${time.toString().padStart(2, '0')}HRS`;
                }
            },
            xAxis: {
                type: 'category',
                data: hours,
                splitArea: {
                    show: true,
                    areaStyle: {
                        color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)']
                    }
                },
                name: 'Hours',
                nameLocation: 'end',
                nameGap: 10,
                axisLabel: {
                    interval: 0,
                    rotate: 45,
                    margin: 12,
                    color: '#000',
                    fontSize: 10
                },
                axisLine: {
                    lineStyle: {
                        color: '#000',
                        width: 1,
                    }
                },
                axisTick: {
                    alignWithLabel: true,
                    length: 5,
                    lineStyle: {
                        color: '#000',
                        width: 1,
                    }
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: '#ddd',
                        width: 1,
                        type: 'solid'
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: days,
                splitArea: {
                    show: false,
                    areaStyle: {
                        color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)']
                    }
                },
                axisTick: {
                    alignWithLabel: true,
                    length: 5,
                    lineStyle: {
                        color: '#000',
                        width: 1,
                    }
                },
                name: 'Day',
                nameLocation: 'end',
                nameGap: 75,
                axisLabel: {
                    color: '#000',
                    margin: 8,
                    fontSize: 10
                }
            },
            visualMap: {
                type: 'continuous',
                min: 0,
                max: 100,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '30%',
                text: ['High', 'Low'],
                inRange: {
                    color: ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39']
                },
                outOfRange: {
                    color: ['#ebedf0']
                },
                dimension: 2,
                show: false
            },
            grid: {
                width: "100%",
                //height: "50px"
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
                containLabel: true,
            },
            series: [{
                name: 'Events',
                type: 'heatmap',
                data: data || [],
                label: {
                    show: true,
                    formatter: (params) => formatNumber(params.data[2]),
                    fontSize: 10
                },
                itemStyle: {
                    borderColor: '#fff',
                    margin: 5,
                    borderCap: "round",

                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        borderWidth: 1,
                        borderRadius: 5
                    }
                },
                progressive: 1000,
                animation: false
            }]
        };
    };

    const handleChartClick = async (params) => {
        const startDate = DateTime.fromMillis(params.data[4].ts);
        const endDate = startDate.plus({ hours: 1 });
        const response = await (await APIInvoke.invokeGET(`/projects/${project}/dashboard/events/detail/hourly?start=${startDate.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss")}&end=${endDate.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss")}`)).json();
        setTimelapse(response.body)
        setShowTimelapseDialog({
            show: true,
            startDate: startDate,
            endDate: endDate
        })
    }

    const handleClickTimelapse = (lapse) => {
        const startDate = showTimelapseDialog.startDate
        const endDate = showTimelapseDialog.endDate;
        const collection = lapse._id.collection;

        const queryParam = {
            "query": {
                "$and": [
                    {
                        "lastReceivedAt": {
                            "$gte": `ISODate("${startDate.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ")}")`
                        }
                    },
                    {
                        "lastReceivedAt": {
                            "$lte": `ISODate("${endDate.toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZZ")}")`
                        }
                    }
                ]
            },
            "queryBuilder": {
                "combinator": "and",
                "rules": [
                    {
                        "field": "lastReceivedAt",
                        "operator": ">=",
                        "value": startDate.toFormat("yyyy-MM-dd'T'HH:mm"),
                        "id": "5e00e8bd-c99e-44ac-bb89-3ea3589b23d1"
                    },
                    {
                        "field": "lastReceivedAt",
                        "operator": "<=",
                        "valueSource": "value",
                        "value": endDate.toFormat("yyyy-MM-dd'T'HH:mm"),
                        "id": "1ca020bb-67b5-413a-8148-eae117c9b201"
                    }
                ],
                "id": "4cf14825-d545-47a2-b83f-bb7653e2dabf"
            }
        }

        const queryParam64 = btoa(JSON.stringify(queryParam));

        navigate(`/projects/${project}/collections/${collection}?query=${queryParam64}`, { state: { backLink: location.pathname } });
    };

    return (
        <div >
            <ConfirmDialog color="green" title="Timelapse detail" open={showTimelapseDialog.show} confirmMessage="OK" onConfirm={() => setShowTimelapseDialog({ ...showTimelapseDialog, show: false })} onClose={() => setShowTimelapseDialog({ ...showTimelapseDialog, show: false })}>
                {timelapse.length === 0 && <p>No events found</p>}
                {timelapse.map(lapse => (
                    <div className="flex justify-between p-2 items-center gap-8 min-w-80 hover:bg-gray-100">
                        <p className="text-sm">{lapse._id.collection}: {lapse.eventCount}</p>
                        <IconButton onClick={() => handleClickTimelapse(lapse)} color="primary" size="small" ><LaunchIcon fontSize="small" /></IconButton>
                    </div>
                ))}
            </ConfirmDialog>
            <ReactECharts
                option={getOption()}
                showLoading={loading}
                onEvents={{
                    click: handleChartClick
                }}
            />
        </div >

    );
}
export default Heatmap2d;